
import { Component, Model, Prop, Vue, Emit } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { NotifyNotificationRules, NotifySkipped, NotifyRuleReload, NotifySent } from "@/store/site-monitor/types";
import NotificationModalContent from "@/components/site-monitor/NotificationModalContent.vue";
import { BModal } from 'bootstrap-vue';
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import eventsApi from "@/services/api.event.service";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";

@Component({
	components: {
		"notification-modal-content": NotificationModalContent
	}
})
export default class NotificationModal extends Vue {
	$refs!: {
		notificationModal: BModal;
	};

	@Prop({ type: Number }) readonly groupId: number;
	@Prop({ type: Number }) readonly eventId: number;
	@Model("change", { type: Boolean }) readonly isVisible: boolean;

	@Prop({type: Boolean, required: false, default: true})
	private notifyOpenedManually: boolean;

	@Prop({ type: Object, required: false})
	private notifyNotificationRules: NotifyNotificationRules;

	@Prop({ type: Boolean, required: false, default: false })
	private isPoppedOut: boolean;

	@Prop({ type: Boolean, required: false, default: false })
	private isEventUpdate: boolean;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	private emailsLoading: boolean = false;
	private eventOutcome: EventOutcomeAndNote = {
		eventOutcomeId: null,
		eventOutcomeNote: null,
		valid: true
	};
	private selectedNotificationTemplate: NotificationTemplate | null = null;

	private modalSizeClass: string = 'lg';

	@Emit("change")
	private onModalVisibilityChange(isVisible: boolean): boolean {
		return isVisible;
	}

	@Emit("notify-skipped")
	private skipNotify(notifySkipped: NotifySkipped): NotifySkipped {
		return notifySkipped;
	}

	private get dontCloseOnBackDrop(): boolean {
		const enforceDismissingByButtons = this.getFeature(["TenantSettings", "EnforceDismissingModalByButtons"]);
  		return enforceDismissingByButtons || !this.notifyOpenedManually;
	}

	private get isPersistEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	@Emit("close-modal")
	private async closeModal(): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled) {
			await this.updateEventOutcome();
		}
	}

	@Emit("notify-rule-reload")
	private emitNotifyRuleReload(notifyRuleReload: NotifyRuleReload): NotifyRuleReload {
		return notifyRuleReload;
	}

	@Emit("notify-sent")
	private sentNotification(notifySent: NotifySent): NotifySent {
		return notifySent;
	}

	private onEmailsLoading(isLoading: boolean): void {
		this.emailsLoading = !isLoading;
	}

	private async onEventOutcomeChange(value: EventOutcomeAndNote): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled) {
			this.eventOutcome = value;
		}
	}

	private async updateEventOutcome(): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled && !this.isEventUpdate && this.selectedNotificationTemplate != null) {
			await eventsApi.preserveEventOutcome(this.eventId, 
				this.eventOutcome?.eventOutcomeId ?? null,
				this.eventOutcome?.eventOutcomeNote ?? null
			);
		}
	}

	private onTemplateChanged(newTemplate: NotificationTemplate): void {
		this.selectedNotificationTemplate = newTemplate;
	}

	private get isNotificationModalLayoutEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "RearrangedLayout"]);
	}

	private get notifyRules(): NotificationRule[] {
		return this.notifyNotificationRules?.rules ?? null;
	}

	private updateModalLayout(colCount: number): void {
		if (!this.isNotificationModalLayoutEnabled) {
			this.modalSizeClass = 'lg';
			return;
		}

		if (colCount >= 2) {
			this.modalSizeClass = 'xl';
		} else {
			this.modalSizeClass = 'lg';
		}												
	}

	private get notificationRuleTooltip(): string {
		if (!this.notifyRules) {
			return "";
		}

		return `Contacts have been assigned using the following Notification Rules: ${this.notifyRules.map(rule => rule.title).join(", ")}`;
	}
}
